import { Avatar, Typography, Box } from '@mui/material';
import { t } from 'i18next';
import React, { memo, useMemo } from 'react';
import { getSyncStatus } from 'shared/utils/sync';

import { TeamIcon } from '@/assets/svg';
import PremiumStarIcon from '@/assets/svg/PremiumStarIcon';
import { SyncStatusBadge } from '@/components';
import { useDBSyncContext } from '@/context/DBSyncContext';
import { useSubscriptionContext } from '@/context/SubscriptionContext';
import { useIsPremiumFeatureEnabled, useNetwork } from '@/hooks';
import { COLOR, ENTITY_COLOR } from '@/theme/colors';

import { Props } from './types';

function CurrentTeamBox({ organisationName, avatarUrl, isTeamOwner }: Props) {
    const { currentOrganisationSubscriptionPlan, isCurrentSubscriptionActive } =
        useSubscriptionContext();
    const isOnline = useNetwork();

    const { isSyncInProgress, hasSyncIssue, isUnsynced } = useDBSyncContext();
    const { isPremiumFeatureEnabled } = useIsPremiumFeatureEnabled();

    const syncStatus = useMemo(
        () =>
            getSyncStatus({
                hasSyncIssue,
                isOnline,
                isSyncInProgress,
                isUnsynced,
            }),
        [hasSyncIssue, isUnsynced, isOnline, isSyncInProgress],
    );

    const isTeamSubscription =
        !!currentOrganisationSubscriptionPlan &&
        currentOrganisationSubscriptionPlan?.seats > 1;

    return (
        <Box
            className="flex relative pt-3 w-full h-full"
            data-test-id="OrganisationBox"
            id="organisations-menu-button"
        >
            <Box
                sx={{
                    fontSize: 28,
                    fontWeight: 700,
                    height: 60,
                    width: 60,
                    border: '2px solid',
                    borderColor: ENTITY_COLOR.teams,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                }}
                className="mx-3 rounded-lg"
            >
                <Avatar
                    data-test-id="OrganisationBox-Avatar"
                    src={avatarUrl}
                    sx={{
                        width: 52,
                        height: 52,
                        borderRadius: 0.65,
                        backgroundColor: COLOR.eclipse,
                    }}
                    variant="square"
                >
                    <TeamIcon height={40} width={40} color={'grey'} />
                </Avatar>

                {isCurrentSubscriptionActive && isPremiumFeatureEnabled && (
                    <Box className="absolute top-[-4px] right-[-8px]">
                        <Box className="relative z-[1]">
                            <PremiumStarIcon
                                width={18}
                                height={18}
                                color={
                                    isTeamSubscription
                                        ? ENTITY_COLOR.teams
                                        : COLOR.deepCerulean
                                }
                                secondaryColor={COLOR.white}
                            />
                        </Box>
                        <Box className="absolute inset-0 bg-white h-[18px] w-[18px] rounded-full" />
                    </Box>
                )}
            </Box>
            <Box>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        maxWidth: 105,
                    }}
                >
                    <Typography
                        data-test-id="OrganisationBox-OrganisationNameText"
                        fontSize={13}
                        fontWeight={700}
                        variant="body2"
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            lineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                        }}
                    >
                        {organisationName}
                    </Typography>
                </Box>
                {isTeamOwner ? (
                    <Typography
                        sx={{
                            fontSize: 11,
                            fontWeight: 400,
                            color: COLOR.black89,
                        }}
                    >
                        {t('Organisations:labels:your_team')}
                    </Typography>
                ) : null}
                <SyncStatusBadge status={syncStatus} />
            </Box>
        </Box>
    );
}

export default memo(CurrentTeamBox);
